import React, { Suspense } from 'react';
import { useIntl } from 'react-intl';
import { useAgent } from './AgentProvider';

// Widget component registry - dynamically imports widgets as needed
const WidgetRegistry = {
  // Succession-specific agent widgets
  'succession_planner': React.lazy(() => import('./widgets/SuccessionPlannerWidget')),
  'valuation_advisor': React.lazy(() => import('./widgets/ValuationAdvisorWidget')),
  'deal_helper': React.lazy(() => import('./widgets/DealHelperWidget')),
  'bcp_assistant': React.lazy(() => import('./widgets/BcpAssistantWidget')),
  'financial_analyzer': React.lazy(() => import('./widgets/FinancialAnalyzerWidget')),
  'document_helper': React.lazy(() => import('./widgets/DocumentHelperWidget')),
  'market_insights': React.lazy(() => import('./widgets/MarketInsightsWidget')),
  'content_creation_copilot': React.lazy(() => import('./widgets/ContentCreationCopilotWidget')),
  
  // Generic utility widgets
  'chat_interface': React.lazy(() => import('./widgets/ChatInterfaceWidget')),
  'form_assistant': React.lazy(() => import('./widgets/FormAssistantWidget')),
  'notification_panel': React.lazy(() => import('./widgets/NotificationPanelWidget')),
  'quick_actions': React.lazy(() => import('./widgets/QuickActionsWidget')),
  'context_help': React.lazy(() => import('./widgets/ContextHelpWidget'))
};

// Widget position classes
const PositionClasses = {
  'top-left': 'fixed top-4 left-4 z-50',
  'top-right': 'fixed top-4 right-4 z-50',
  'top-center': 'fixed top-4 left-1/2 transform -translate-x-1/2 z-50',
  'bottom-left': 'fixed bottom-4 left-4 z-50',
  'bottom-right': 'fixed bottom-4 right-4 z-50',
  'bottom-center': 'fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50',
  'center': 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50',
  'contextual': 'relative z-10 my-4',
  'inline': 'inline-block',
  'sidebar': 'fixed right-0 top-0 h-full w-80 z-40',
  'modal': 'fixed inset-0 z-50 flex items-center justify-center'
};

// Loading component for lazy-loaded widgets
const WidgetLoader = ({ type }) => {
  const intl = useIntl();
  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-md p-4 animate-pulse">
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 bg-succession-primary rounded-full animate-ping"></div>
        <div className="text-succession-text-secondary text-sm">
          {intl.formatMessage({ id: 'succession.agent_system.agent_widget_renderer.loading_agent' }, { type: type.replace('_', ' ') })}
        </div>
      </div>
    </div>
  );
};

// Individual widget wrapper
const WidgetWrapper = ({ widget, onClose }) => {
  const { id, type, props, position, context, agentId } = widget;
  
  // Get the widget component from registry
  const WidgetComponent = WidgetRegistry[type];
  
  if (!WidgetComponent) {
    console.warn(`Widget type "${type}" not found in registry`);
    return null;
  }

  // Position classes
  const positionClass = PositionClasses[position] || PositionClasses['bottom-right'];
  
  // Backdrop for modal widgets
  const showBackdrop = position === 'modal';

  return (
    <>
      {showBackdrop && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => onClose(id)}
        />
      )}
      
      <div className={positionClass}>
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-lg transition-all duration-300 relative">
          {/* Close button */}
          <button
            onClick={() => onClose(id)}
            className="absolute top-2 right-2 z-10 text-succession-text-secondary hover:text-succession-text-primary transition-colors p-1 rounded hover:bg-succession-bg-secondary"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Widget content */}
          <Suspense fallback={<WidgetLoader type={type} />}>
            <WidgetComponent
              widgetId={id}
              agentId={agentId}
              context={context}
              onClose={() => onClose(id)}
              {...props}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
};

// Main widget renderer component
const AgentWidgetRenderer = () => {
  const { spawnedWidgets, closeWidget } = useAgent();

  // Don't render anything if no widgets are spawned
  if (!spawnedWidgets || spawnedWidgets.length === 0) {
    return null;
  }

  return (
    <>
      {spawnedWidgets.map((widget) => (
        <WidgetWrapper
          key={widget.id}
          widget={widget}
          onClose={closeWidget}
        />
      ))}
    </>
  );
};

// Context-aware widget spawner hook
export const useContextualSpawning = () => {
  const { spawnWidget, currentContext } = useAgent();

  const spawnContextualWidget = (widgetType, additionalProps = {}) => {
    // Determine position based on context
    let position = 'bottom-right';
    
    // Context-based positioning logic
    if (currentContext.component === 'succession_dashboard') {
      position = 'bottom-right';
    } else if (currentContext.component === 'deal_room') {
      position = 'sidebar';
    } else if (currentContext.component === 'bcp_form') {
      position = 'contextual';
    }

    // Context-aware props
    const contextProps = {
      currentPage: currentContext.url,
      userContext: currentContext.user,
      timestamp: Date.now(),
      ...additionalProps
    };

    return spawnWidget(widgetType, contextProps, position);
  };

  return { spawnContextualWidget };
};

// Agent widget trigger component - can be placed anywhere
export const AgentTrigger = ({
  agentType = 'succession_planner',
  widgetType = 'chat_interface',
  trigger = 'button',
  children,
  className = ''
}) => {
  const intl = useIntl();
  const { spawnContextualWidget } = useContextualSpawning();

  const handleTrigger = () => {
    spawnContextualWidget(widgetType, { agentType });
  };

  if (trigger === 'auto') {
    // Auto-trigger based on context (useEffect would handle this)
    return null;
  }

  return (
    <button
      onClick={handleTrigger}
      className={`inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 bg-succession-primary text-white hover:bg-succession-primary-hover shadow-md hover:shadow-lg ${className}`}
    >
      {children || (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span>{intl.formatMessage({ id: 'succession.agent_system.agent_widget_renderer.ai_assistant' })}</span>
        </>
      )}
    </button>
  );
};

export default AgentWidgetRenderer;